<template>
  <div
    :class="[
      $style.base,
      {
        [$style.horizontalContent]: horizontalContent,
        [$style.isSmall]: $screen === 's',
        [$style.isLoading]: loading,
        [$style.headerMargin]: headerMargin,
        [$style.hasMaxHeight]: !!maxContentHeight,
        [$style.hasHeader]: showHeader,
        [$style.gray]: gray,
        [$style.isSelected]: selected,
        'cy-selected': selected,
        [$style.isClosable]: closable,
        [$style.fullHeight]: fullHeight,
        [$style.fullWidth]: fullWidth,
        [$style.noPadding]: noPadding,
        [$style.hasTabs]: !!tabs.length,
        [$style.collapsed]: (collapsible && collapsed) || (withToggle && !toggle),
        [$style.hasError]: error,
      }
    ]"
  >
    <div
      v-if="closable"
      :class="$style.close"
    >
      <BaseIcon
        name="close"
        @click="$emit('close')"
        v-test="'btn-close'"
      />
    </div>
    <div
      v-if="showHeader"
      :class="$style.header"
    >
      <div :class="$style.headerInner">
        <div :class="$style.headerTop">
          <div
            v-if="!!tabs.length"
            :class="$style.tabs"
          >
            <div
              v-for="(tab, index) in tabs"
              ref="tabsRef"
              :key="`tab-${index}`"
              :class="[
              $style.tab,
              {
                [$style.active]: tab.active
              }
            ]"
              @click="onTabClick(tab)"
              v-test="`tab-${tab.id}`"
            >
              {{ tab.label }}
            </div>
            <div
              :class="$style.tabsLine"
              :style="{ width: lineWidth + 'px', left: lineLeft + 'px' }"
            />
          </div>
          <div
            v-else-if="heading || label"
            :class="$style.heading"
          >
            <BaseHeading
              v-if="heading"
              :info="info"
            >
              {{ heading }}
            </BaseHeading>
            <BaseNotification
              v-if="notification"
              color="primary"
              size="m"
            >
              {{ notification }}
            </BaseNotification>
            <div
              v-if="label"
              :class="$style.label"
            >
              <BaseLabel :state="label" />
            </div>
          </div>
          <div
            v-if="$slots.header"
            :class="[
            $style.headerSlot,
            {
              [$style.headerRight]: !!heading
            }
          ]"
          >
            <slot name="header" />
          </div>
          <div
            v-if="collapsible"
            :class="$style.collapse"
          >
            <BaseIcon
              :name="collapsed ? 'chevron-down' : 'chevron-up'"
              @click="collapsed = !collapsed"
            />
          </div>
          <div
            v-if="withToggle"
            :class="$style.collapse"
          >
            <BaseToggle v-model="toggle" />
          </div>
        </div>
        <BaseText
          v-if="description"
          color="secondary"
        >
          {{ description }}
        </BaseText>
      </div>
    </div>
    <div
      :class="$style.contentWrap"
      :style="
        maxContentHeight ? { 'max-height': `${maxContentHeight}px` } : null
      "
    >
      <div
        :class="$style.inner"
        :style="{ minHeight: minLoadingHeight !== undefined && loading ? `${minLoadingHeight}px` : null }"
      >
        <BaseSpinner v-if="loading" />
        <slot v-else />
      </div>
    </div>
    <div
      v-if="$slots.footer"
      :class="$style.footer"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { computed, nextTick, ref, useSlots, watch } from 'vue';

defineOptions({ inheritAttrs: false });

const slots = useSlots();

const props = withDefaults(
  defineProps<{
    closable?: boolean;
    horizontalContent?: boolean;
    heading?: string;
    label?: string;
    headerMargin?: boolean;
    loading?: boolean;
    tabs?: { id: string; label: string; active: boolean }[];
    maxContentHeight?: number;
    gray?: boolean;
    selected?: boolean;
    fullHeight?: boolean;
    fullWidth?: boolean;
    noPadding?: boolean;
    collapsible?: boolean;
    notification?: number;
    minLoadingHeight?: number;
    info?: string;
    toggle?: boolean;
    error?: boolean;
    description?: string;
  }>(),
  {
    closable: false,
    horizontalContent: false,
    heading: '',
    label: '',
    headerMargin: true,
    loading: false,
    tabs: () => [],
    gray: false,
    selected: false,
    fullHeight: false,
    fullWidth: false,
    noPadding: false,
    collapsible: false,
    notification: 0,
    maxContentHeight: 0,
    toggle: undefined,
    error: false,
    description: '',
  },
);

const emit = defineEmits(['close', 'update:toggle']);

const lineWidth = ref(0);
const lineLeft = ref(0);
const tabsRef = ref<HTMLElement[]>([]);

const onTabClick = (tab: { id: string; label: string; active: boolean }) => {
  props.tabs?.forEach((tab) => {
    tab.active = false;
  });
  tab.active = true;
};

const collapsed = ref(false);

const withToggle = ref(typeof props.toggle === 'boolean');
const toggle = computed({
  get() {
    return !!props.toggle;
  },
  set(value: boolean) {
    emit('update:toggle', value);
  },
});

watch(
  () => props.tabs,
  () => {
    nextTick(() => {
      setLinePosition();
    });
  },
  { deep: true, immediate: true },
);

const showHeader = computed(
  () =>
    !!props.heading ||
    !!props.tabs.length ||
    !!slots.header ||
    props.collapsible ||
    withToggle.value,
);

const activeTabIndex = computed(
  () => props.tabs?.findIndex((tab) => tab.active) ?? -1,
);

const setLinePosition = () => {
  if (!tabsRef.value.length) {
    return;
  }

  if (activeTabIndex.value >= 0) {
    const activeLinkEl = tabsRef.value[activeTabIndex.value];

    if (activeLinkEl) {
      lineWidth.value = activeLinkEl.clientWidth;
      lineLeft.value = activeLinkEl.offsetLeft;
    }
  }
};
</script>

<style
  lang="scss"
  module
>
$closeButtonSize: 20px + $spacing;

.base {
  position: relative;
  border-radius: $radius * 2;

  // Nested cards get the smaller radius
  .base & {
    border-radius: $radius;
  }

  &:not(.gray) {
    background-color: var(--surface-primary);
    border: 1px solid var(--border);
  }

  &.gray {
    background-color: var(--surface-secondary);
    border: 1px solid var(--border);
  }

  &.isSelected {
    border-color: var(--action-primary) !important;
  }

  &.fullHeight {
    height: 100%;
    overflow-y: auto;
  }

  &.fullWidth {
    width: 100%;
  }

  @media print {
    padding: 0 !important;
  }

  &.hasError {
    border-color: var(--error-base);
  }
}

.contentWrap {
  .base.hasMaxHeight & {
    overflow-y: auto;
  }
}

.inner {
  position: relative;
  padding: $spacing;

  .base.isClosable:not(.hasHeader) & {
    padding-right: $closeButtonSize;
  }

  .base.hasHeader:not(.headerMargin) & {
    padding-top: 0;
  }

  .base.isLoading & {
    min-height: 89px;
  }

  .base.horizontalContent & {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    margin-bottom: $spacing * -1;

    & > * {
      margin-bottom: $spacing;

      &:not(:last-child) {
        margin-right: $spacing;
      }
    }
  }

  .base.horizontalContent.isSmall & {
    flex-wrap: wrap;

    & > * {
      &:not(:last-child) {
        margin-bottom: $spacing;
      }
    }
  }

  .base.collapsed & {
    display: none;
  }
}

// Specific selector to fix issue with nested BaseCard components
.base.noPadding > .contentWrap > .inner {
  padding: 0;
}

.header {
  padding: 0 $spacing;

  .base.isClosable & {
    padding-right: $closeButtonSize;
  }
}

.headerInner {
  display: flex;
  flex-direction: column;
  gap: $spacing * .25;
  padding: $spacing 0;
  
  .base:not(.collapsed) & {
    border-bottom: 1px solid var(--border);
  }
}

.headerTop {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  justify-content: space-between;
  align-items: center;

  .base.hasTabs & {
    align-items: stretch;
  }
}

.headerSlot {
  display: flex;
  align-items: center;
}

.headerRight {
  text-align: right;
}

.footer {
  border-top: 1px solid var(--border);
  padding: $spacing;
}

.tabs {
  position: relative;
  display: flex;
  padding-left: $spacing * 0.5;
}

.tabsLine {
  position: absolute;
  bottom: $spacing * -1;
  left: 0;
  transition:
    left 0.2s $easeOutCubic,
    width 0.2s $easeOutCubic;
  width: 0;
  padding: 0 $spacing * 0.5;

  &:after {
    content: '';
    display: block;
    height: 2px;
    background-color: var(--accent);
  }
}

.tab {
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  margin: $spacing * -1;
  padding: $spacing;

  color: var(--text-secondary);

  &:not(:last-child) {
    margin-right: $spacing;
  }

  &.active {
    color: var(--text-primary);
  }

  @include hover {
    color: var(--text-primary);
  }
}

.heading {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
}

.label {
  margin: -2px 0;
}

.close {
  position: absolute;
  right: 0;
  top: 0;
  padding: $spacing * 0.25;
  z-index: 1;
  width: $closeButtonSize;
  height: $closeButtonSize;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
